import AnswerModel from './answer/answer-model';
import { WordCloud } from '@/word-cloud/interfaces';
import { get } from 'lodash';

export declare type WordCloudAnswerModel = WordCloud.Word;

export declare type WordCloudConfig = Partial<WordCloud.Config>;

export enum PollType {
    MULTIPLE_CHOICE = 'multiple_choice',
    WORD_CLOUD = 'word_cloud',
}

interface Poll {
    poll_id: string,
    poll_type?: PollType,
    voting_opened_timestamp?: number,
    fp_created_timestamp?: number,
    countdown_duration?: number,
    execution_id: string, // this is kind of redundant with the poll_id, but we need it for legacy requests
    question: string,
    show_results: boolean
}

export interface MultipleChoicePoll extends Poll {
    poll_type?: PollType.MULTIPLE_CHOICE,
    rows?: AnswerModel[][]
    answers: AnswerModel[]
    results: Record<string, number>
}

export interface WordCloudPoll extends Poll {
    poll_type: PollType.WORD_CLOUD,
    answers?: WordCloudAnswerModel[]
    results: WordCloudAnswerModel[]
}

export type PollInterface = MultipleChoicePoll | WordCloudPoll;

export interface PollsConfig {
    showResultsOnly: boolean,
    large: boolean,
    labels: Record<string, string>,
    colors: WordCloud.Colors,
    readOnly?: boolean,
    font?: {
        sizeRange: [number, number],
        family: string,
        weight: number | string,
    },
    resultsColor?: string
}

export default class PollsController {

    private parseMultiChoiceAnswers(details: Partial<MultipleChoicePoll>): AnswerModel[] {
        const total = this.getTotalVotes(get(details, 'results', {}));
        const getResult = (id: string) =>
            get(details, `results.${id}`, 0);

        return (get(details, 'answers', []) as AnswerModel[])
            .map((answer: AnswerModel) => ({
                ...answer,
                votePercentage: `${(((getResult(answer.id) / total) || 0) * 100).toFixed(2)}%`
            }));
    }

    private parseWordcloudAnswers(details: Partial<WordCloudPoll>): WordCloudAnswerModel[] {

        return get(details, 'results', []);
    }

    public parseAnswers(details: Partial<PollInterface>): Array<AnswerModel | WordCloudAnswerModel> {
        return details.poll_type === PollType.WORD_CLOUD
            ? this.parseWordcloudAnswers(details)
            : this.parseMultiChoiceAnswers(details as Partial<MultipleChoicePoll>);
    }

    private getTotalVotes(results: Record<string, number>): number {
        return Object.keys(results)
            .reduce((acc, x) => acc + results[x], 0);
    }
}
